import { environment, token } from '@gaodun.com/gtools';
import { appId } from '@/utils/config';
import { IFunctionCall, IToolCall, getUuid, getSign, createQaRecordId, toastInQa } from '@/utils/qa';
import { update_luca_ask_record, create_luca_ask_record } from '@/apis/qa';
import { getQaStore } from '@/store/qa';
import { getGlobalStore } from '@/store/global';
import { CancelToken } from 'axios';
import { IQaRecordItem } from '../qa-records';
import { IAskChatRecord, IGuessYouAskParams, IQaPairDetail, TRecordType } from '@/types';
import { convertKeysToCamelCase, delay, formatSecodes } from '@/utils';
import moment from 'moment';
import { IMode } from '../dialogue-tool/index.helper';
import { createSubjectSearchCardSensors } from '@/utils/sensors';

export interface IReadStreamParams {
    lucaRecordId: string;
    question: string;
    answer: string;
    toolCalls: IToolCall[];
    returnSuccess: boolean;
    lucaAskChatId: string;
    botChatIdOfStream: string;
    isCreateQa: boolean; // 是否为首次提问，创建答疑
    images?: string[];
    functionCall?: IFunctionCall;
    preStreamMessage?: string;
}

export interface ICreateRecordOptions {
    /**
     * 是否需要接口错误处理
     */
    needErrorHandle?: boolean;
    /**
     * 请求完成的回调
     */
    requestFinish?: (success?: boolean) => void;
    /**
     * 取消请求
     */
    cancelToken?: CancelToken;
    /**
     * 执行Id
     */
    excuteId?: string;
}

interface IOptions {
    /**
     * 第一条记录是否展示日期
     */
    showDateByFirstRecord?: boolean;
    /**
     * 是否已解决卡片的title
     */
    whetherSolveTitle?: string;
    /**
     * 创建卡片的参数recordType类型
     */
    createRecordType?: TRecordType;
}
/**
 * 转换新的答疑记录
 */
export const transformToQaRecords = (list: IAskChatRecord[], options: IOptions = {}) => {
    const { showDateByFirstRecord, whetherSolveTitle, createRecordType } = options;
    let isLastQaAgain: boolean | undefined;
    let newestLucaAskChatId: string | undefined;
    let guessYouAskParams: { recordType: 50; guessYouQuestion: IGuessYouAskParams } | undefined;
    const newList = list || [];
    const lastAiRecordIndex = newList.length - [...newList].reverse().findIndex(item => item.recordType === 15) - 1;
    const records = newList.reduce((total, item, index) => {
        const { recordType, recordId, lucaAskChatId, createdAt, content, lastAskAgain } = item;
        if (!newestLucaAskChatId) newestLucaAskChatId = lucaAskChatId;
        if (!isLastQaAgain) isLastQaAgain = !!lastAskAgain;
        switch (recordType) {
            case 10:
            case 22:
                total.push({
                    recordId,
                    role: 'questioner',
                    type: 'basic',
                    content,
                    pictures: item.images || [],
                    createdAt: showDateByFirstRecord && index === 0 ? item.createdAt.slice(0, 16) : undefined,
                });
                break;
            case 14:
            case 25:
                total.push({ recordId, role: 'respondent', type: 'teacherReply', content: item.content });
                break;
            case 15:
                if (!item.content) return total;
                total.push({
                    recordId,
                    role: 'respondent',
                    type: 'ai',
                    content: item.content,
                    feedbackType: item.feedbackType ? item.feedbackType : undefined,
                    isFeedbackEvaluation: item.feedbackState === 1,
                    permanentActionBar: index === lastAiRecordIndex,
                });
                break;
            case 16:
                if (createRecordType === 46) {
                    guessYouAskParams = { recordType: 50, guessYouQuestion: { scene: 3 } };
                }
                total.push({ recordId, role: 'respondent', type: 'streamTips', content });
                break;
            case 17:
                total.push({ recordId, role: 'system', type: 'whetherSolve', state: 2, title: whetherSolveTitle });
                break;
            case 18:
                total.push({ recordId, role: 'system', type: 'whetherSolve', state: 3, title: whetherSolveTitle });
                break;
            case 19:
                total.push({ recordId, role: 'system', type: 'teacherQaTips', date: createdAt.slice(0, 16) });
                break;
            case 20:
            case 30:
                {
                    const info = item.voiceResponseCard;
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'voiceReply',
                        params: {
                            audioKey: `audio_key_${getUuid()}`,
                            linkContent: info.voiceContent,
                            linkFile: info.voiceUrl,
                            linkTime: info.times,
                        },
                    });
                }
                break;
            case 21:
            case 35:
                {
                    const dataSource = item?.similarProblems || item.helpYouCard?.similarProblems || {};
                    const questions = dataSource.items || [];
                    if (questions.length) {
                        total.push({
                            recordId,
                            role: 'respondent',
                            type: 'similarQuestion',
                            dataSource: JSON.parse(JSON.stringify(dataSource)),
                            list: questions.map(item => {
                                return {
                                    askId: item.askId,
                                    ask: item.ask
                                        .replace(/<em>/g, '<span class="similar-search-drawer__question-describe--mark">')
                                        .replace(/<\/em>/g, '</span>'),
                                };
                            }),
                        });
                    }
                }
                break;
            case 40:
                {
                    const ocrContent = item.askOcrContent?.ocrContent || '';
                    guessYouAskParams = { recordType: 50, guessYouQuestion: { scene: 0, question: ocrContent, saveFlag: false } };
                    total.push({
                        recordId,
                        role: 'questioner',
                        type: 'ocr',
                        content: ocrContent,
                        picture: item.images?.[0],
                        hideOcrContentEditBtn: !item.askOcrContent?.editState,
                    });
                }
                break;
            case 45:
                {
                    const picAnswer = item.picAnswer;
                    const searchContent = (picAnswer?.selectList || []).reduce((richText, item) => {
                        return richText + `<br>${item.option}、${item.content}`;
                    }, (picAnswer?.title || '') + (picAnswer?.littleTitle ? `<br>${picAnswer!.littleTitle}` : ''));
                    const questionId = picAnswer?.questionId || 0;
                    const littleQuestionId = picAnswer?.littleQuestionId;
                    guessYouAskParams = {
                        recordType: 50,
                        guessYouQuestion: { scene: 0, question: searchContent, questionId, littleQuestionId },
                    };
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'questionOfOcrSearch',
                        similarity: picAnswer?.similarity || 0,
                        content: searchContent,
                        questionId,
                        littleQuestionId,
                        hideReplaceOcrResultBtn: !picAnswer?.showButton,
                        everyAsks: picAnswer?.everyAsks || [],
                    });
                }
                break;
            case 50:
                {
                    const questions = Object.values(item.guessYouQuestion?.guessYouQuestions || {});
                    if (questions.length) {
                        total.push({
                            recordId,
                            role: 'respondent',
                            type: 'guessYouAsk',
                            questions,
                            relateId: item.guessYouQuestion?.relateId || 0,
                        });
                    }
                }
                break;
            case 60:
                total.push({ recordId, role: 'respondent', type: 'rechargeLucaQa' });
                break;
            case 61:
                total.push({ recordId, role: 'respondent', type: 'rechargeTeacherQa' });
                break;
            case 65:
                {
                    const info = item.subjectSearch;
                    const qaList = info?.qaList || [];
                    const knowledgeSectionList = info?.knowledgeSectionList || [];
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'subjectSearch',
                        dataSource: info && JSON.parse(JSON.stringify(info)),
                        sensorsData: {
                            lucaAskChatId,
                            recordId,
                            qaNum: qaList.length,
                            videoNum: knowledgeSectionList.filter(item => item.resourceType === 0).length,
                            knowledgePointsNum: knowledgeSectionList.filter(item => item.resourceType === 1).length,
                        },
                        qaList,
                        knowledgeSectionList,
                    });
                }
                break;
            default:
                break;
        }
        return total;
    }, [] as IQaRecordItem[]);
    return {
        success: true,
        newestLucaAskChatId,
        isLastQaAgain,
        records,
        guessYouAskParams,
    };
};

const getTypeAndIdBySource = (source: any): { sourceType?: 'video' | 'question'; id?: string; url?: string } => {
    switch (source.type) {
        case 'video':
            return { sourceType: 'video', id: source.sewise?.sourceId };
        case 'live_new':
            // 精简回放
            if (source.resourceType === 1) {
                return { sourceType: 'video', id: source?.live?.simplifyRecordUrl };
            }
            // 实时回放
            if (source.resourceType === 5) {
                return { sourceType: 'video', url: source?.live?.urlLivePlayBackPc };
            }
            return {};
        case 'smart_school':
            return { sourceType: 'video', id: source?.playbackLink };
        case 'item':
            return { sourceType: 'question', id: source.itemId };
        default:
            return {};
    }
};

/**
 * 根据问题来源数据转换为问题来源记录
 */
export const transformToQuestionSourceRecordBySource = (data: any): IQaRecordItem | null => {
    const source = convertKeysToCamelCase(data);
    // console.log('source', source);
    const { sourceType, id, url } = getTypeAndIdBySource(source);
    if (!sourceType) return null;
    // console.log(source);
    return {
        recordId: createQaRecordId('respondent'),
        role: 'respondent',
        type: 'questionSource',
        sourceType,
        title: (source.sourceName || '').split(' - ').reverse()[0] || '',
        content: `${source.sourceName}${sourceType === 'video' ? '-' + formatSecodes(source.videoTime) : ''}`,
        questionId: id,
        videoId: id,
        url,
        videoTime: source.videoTime,
        permission: source.permission,
    };
};

/**
 * 转换老的答疑记录
 */
export const transformToQaRecordsByOldQaDetail = ({ answer, source, permission }: any) => {
    const records: IQaRecordItem[] = [];
    const questionSourceRecord = transformToQuestionSourceRecordBySource({ ...(source || {}), permission });
    if (questionSourceRecord) {
        records.push(questionSourceRecord);
    }
    return (answer || []).reduce((total: IQaRecordItem[], item, index) => {
        if (item.question_content) {
            total.push({
                recordId: createQaRecordId('questioner'),
                role: 'questioner',
                type: 'basic',
                content: item.question_content,
                pictures: item.images ? JSON.parse(item.images) : [],
            });
        }
        if (index === 0) {
            total.push({
                recordId: createQaRecordId('system'),
                role: 'system',
                type: 'teacherQaTips',
                date: moment(item.ask_time * 1000).format('YYYY-MM-DD HH:mm'),
            });
        }
        if (item.answer_content) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'teacherReply',
                content: item.answer_content,
            });
        }
        if (item.voice_list?.length) {
            item.voice_list.reduce((total: IQaRecordItem[], voiceItem) => {
                total.push({
                    recordId: createQaRecordId('respondent'),
                    role: 'respondent',
                    type: 'voiceReply',
                    params: {
                        audioKey: `audio_key_${getUuid()}`,
                        linkFile: voiceItem.file_link,
                        linkContent: voiceItem.link_content,
                        linkTime: +voiceItem.link_time,
                    },
                });
                return total;
            }, total);
        }
        if (item.more_answer_content) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'teacherReply',
                content: item.more_answer_content,
            });
        }
        if (item.more_file_link) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'voiceReply',
                params: {
                    audioKey: `audio_key_${getUuid()}`,
                    linkContent: item.more_link_content,
                    linkFile: item.more_file_link,
                    linkTime: +item.more_link_time,
                },
            });
        }
        return total;
    }, records);
};

/**
 * 转换Qa对答疑记录
 */
export const transformToQaRecordsByPairOfQaDetail = ({ qaRecords, source, permission, qaId: flashQaId }: IQaPairDetail) => {
    const records: IQaRecordItem[] = [];
    const questionSourceRecord = transformToQuestionSourceRecordBySource({ ...(source || {}), permission });
    if (questionSourceRecord) {
        records.push(questionSourceRecord);
    }
    let flashIndex = 0;
    const newQaRecords = qaRecords || [];
    const list = newQaRecords.reduce((total: IQaRecordItem[], item, index) => {
        let isFlash = false;
        if (qaRecords.length > 1 && item.qaId === flashQaId) {
            flashIndex = index;
            isFlash = true;
        }
        total.push(
            {
                recordId: `questioner_${item.qaId}`,
                role: 'questioner',
                type: 'qaPairQuestioner',
                content: item.question || '',
                isFlash,
            },
            {
                recordId: `respondent_${item.qaId}`,
                role: 'respondent',
                type: 'qaPairReply',
                content: item.answer || '',
                isFlash,
            }
        );
        return total;
    }, records);
    return { flashIndex, records: list };
};

// 断开达模型请求后
export const disconnectGPTAfter = (params: { recordId?: string; botChatIdOfStream?: string; question?: string; answer?: string }) => {
    const { recordId, botChatIdOfStream, question, answer } = params || {};
    const { qaConfig } = getQaStore()!;
    const { botId } = qaConfig;
    update_luca_ask_record({
        recordId: recordId!,
        textStop: {
            botId: botId!,
            chatId: botChatIdOfStream!,
            chatLogs: [
                { role: 'user', content: question || '' },
                { role: 'assistant', content: answer || '' },
            ],
        },
    });
};

// 在完成拍题答疑的情况下，如果再次追问，将ocr识别卡片的可编辑状态置为不可编辑
export const disableOcrEditOrReplace = () => {
    const { setQaRecords, qaRecords } = getQaStore()!;
    console.log('disableOcrEditOrReplace', qaRecords);
    if (qaRecords.some(item => item.type === 'questionOfOcrSearch')) {
        setQaRecords(oldList =>
            oldList.map(item => {
                if (item.type !== 'questionOfOcrSearch') return item;
                return { ...item, hideReplaceOcrResultBtn: true };
            })
        );
    } else if (qaRecords.some(item => item.type === 'ocr')) {
        setQaRecords(oldList =>
            oldList.map(item => {
                if (item.type !== 'ocr') return item;
                update_luca_ask_record({
                    recordId: item.recordId,
                    ocrContent: { ocrContent: item.content, editState: false },
                });
                return { ...item, hideOcrContentEditBtn: true };
            })
        );
    }
};

interface IHandleStatusOfCreateQaRecordsParams {
    status: number;
    needErrorHandle: boolean;
    message?: string;
}

// 创建聊天记录的报错处理
export const handleErrorOfCreateQaRecords = ({ status, needErrorHandle, message }: IHandleStatusOfCreateQaRecordsParams): any => {
    if (status === 0) return null;
    if (!needErrorHandle) return { success: false };
    const { publish } = getGlobalStore()!;
    const { qaConfig, pushQaRecords } = getQaStore()!;
    switch (status) {
        case 10513466: // 提问次数限制
        case 10513468: // 名师追问次数用完
            publish('WarningModal', { type: 'normal', content: message });
            return { success: false };
        /* 追问次数已用完 */
        case 10513467:
            return { success: false, noAskAgainNum: true, message };
        /* 转名师没有权益 */
        case 10513310: {
            qaConfig.aiTag === 1
                ? publish('WarningModal', { type: 'recharge' })
                : publish('WarningModal', { type: 'normal', content: message });
            return { success: false };
        }
        /* 名师答疑没有权益 */
        case 10513476:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'rechargeTeacherQa' });
            return { success: false, needDeleteQuestion: false };
        /* Ai模式，名师追问无权限 */
        case 10513478:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'noPermissionOfProbeByAi' });
            return { success: false, needDeleteQuestion: false };
        /* 非Ai模式，名师追问无权限 */
        case 10513479:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'noPermissionOfProbeByNonAi' });
            return { success: false, needDeleteQuestion: false };
        default:
            toastInQa(message || '');
            return { success: false };
    }
};

export const getChatSseHeaders = async (botKey: string) => {
    const uuid = getUuid();
    const timestamp = new Date().getTime();
    const sign = await getSign({ botKey, uuid, timestamp });
    const accessToken = await token.getFronttoken({ AppID: appId });
    return {
        'Content-Type': 'application/json',
        Authentication: `Basic ${accessToken}`,
        UUID: uuid,
        TIMESTAMP: timestamp,
        SIGN: sign,
    } as any;
};

// 获取创建问题时的视频播放进度时间
export const getVideoTimeOfCreateQuestion = () => {
    const { externalCallbacksRef } = getGlobalStore()!;
    const { qaConfig, updateQaConfig } = getQaStore()!;
    const { lucaAskChatId, resourceType, videoTime } = qaConfig || {};
    // 直播或视频下才需要传递videoTime
    if (!resourceType || ![1, 5].includes(resourceType)) return undefined;
    // 创建过问题，传递详情中的videoTime
    if (lucaAskChatId) return videoTime;
    const time = externalCallbacksRef.current.getVideoTime?.();
    time && updateQaConfig({ videoTime: time });
    return time;
};

export interface ISendToGPTOptions {
    mode?: IMode;
}
export interface IGetResponseOfGPTParams {
    content?: string;
    images?: string[];
    isContinue?: boolean;
    role?: 'function';
    lucaAskChatId?: string;
    functionCallResults?: Record<string, string>;
    retryType?: 1 | 2 /* 1-首问重试 2-追问重试 */;
    promise?: Promise<any>;
}

export const getResponseOfGPT = async (params: IGetResponseOfGPTParams, controller: AbortController) => {
    const {
        lucaAskChatId: oldLucaAskChatId,
        botKey,
        lucaId,
        projectId,
        subjectId,
        twoLevelSubjectId,
        sourceMark,
        clientSource,
        entrySource,
        sceneSource,
        courseId,
        csItemId,
        resourceId,
        resourceType,
        questionTag,
    } = getQaStore()?.qaConfig || {};
    const isCreateQa = !oldLucaAskChatId || params.retryType === 1 ? true : false;
    const question = params.content || '';
    const headers = await getChatSseHeaders(botKey!);
    const response: Response = await fetch(`${environment.getApigateWay()}/athena/api/v1/front/luca-ask/chat-sse`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            equityVersion: 1,
            lucaAskChatId: oldLucaAskChatId,
            lucaId,
            projectId,
            subjectId,
            twoLevelSubjectId,
            sourceMark,
            clientSource,
            entrySource,
            sceneSource,
            courseId,
            csItemId,
            resourceId,
            resourceType,
            videoTime: getVideoTimeOfCreateQuestion(),
            ...params,
        }),
        signal: controller.signal,
    });
    const lucaRecordId = response.headers.get('Luca-Record-Id')!;
    const newestLucaAskChatId = response.headers.get('Luca-Ask-Chat-Id')!;
    const userRecordId = response.headers.get('User-Record-Id')!;
    const isLastAskAgain = response.headers.get('Last-Ask-Again')!;
    const botChatIdOfStream = response.headers.get('Bot-Chat-Id')!;
    const needUpdateUserRecordId = params.role !== 'function' && userRecordId;
    const needRefineProblem = Boolean(
        questionTag !== 1 && newestLucaAskChatId && params.role !== 'function' && !params.isContinue && !params.retryType
    );
    return {
        isCreateQa,
        question,
        newestLucaAskChatId: newestLucaAskChatId || oldLucaAskChatId,
        userRecordId,
        lucaRecordId,
        isLastAskAgain,
        botChatIdOfStream,
        needUpdateUserRecordId,
        needRefineProblem,
        reader: response.body!.getReader(),
    };
};

export const getResponseOfCreateLucaRecord = async (
    lucaAskChatId: string | undefined,
    params: Record<string, any>,
    options?: Partial<{ cancelToken: CancelToken; needErrorHandle: boolean }>
) => {
    try {
        const { qaConfig } = getQaStore()!;
        const { cancelToken, needErrorHandle = false } = options || {};
        const { result, status, message } = await create_luca_ask_record(
            {
                lucaAskChatId,
                equityVersion: 1,
                lucaId: qaConfig.lucaId!,
                sourceMark: qaConfig.sourceMark,
                clientSource: qaConfig.clientSource,
                entrySource: qaConfig.entrySource,
                projectId: qaConfig.projectId,
                subjectId: qaConfig.subjectId,
                twoLevelSubjectId: qaConfig.twoLevelSubjectId,
                sceneSource: qaConfig.sceneSource,
                courseId: qaConfig.courseId,
                csItemId: qaConfig.csItemId,
                resourceId: qaConfig.resourceId,
                resourceType: qaConfig.resourceType,
                videoTime: getVideoTimeOfCreateQuestion(),
                ...params,
            },
            { cancelToken }
        );
        const errorData = handleErrorOfCreateQaRecords({ needErrorHandle, status, message });
        if (errorData) return errorData;
        return transformToQaRecords(result?.list || [], { createRecordType: params.recordType });
    } catch (error) {
        return { success: false, error };
    }
};

const createQuestionSearchPromise = (lucaAskChatId: string, params: Record<string, any>, directExcute?: boolean) => {
    const excuteRef: { resolve?: (isCreate?: boolean) => void; reject?: () => void } = {};
    const promise = new Promise(async (resolve, reject) => {
        excuteRef.resolve = (isCreate?: boolean) => {
            if (isCreate) {
                const source = getQaStore()!.addRequestController<'axios'>('axios');
                resolve(
                    new Promise(async (resolve2, reject2) => {
                        const { records, error } = await getResponseOfCreateLucaRecord(lucaAskChatId, params, {
                            cancelToken: source.token,
                        });
                        records?.length ? resolve2(records) : error === 'send_cancel' ? reject2(error) : resolve2([]);
                    })
                );
            } else {
                resolve([]);
            }
        };
        excuteRef.reject = reject;
    });
    if (directExcute) excuteRef.resolve!(true);
    return { promise, excuteRef };
};

export const excuteQuestionSearchProcess = (params: Record<string, any>, options: Record<string, any>) => {
    const { lucaAskChatId, question } = params;
    const { isCreateQa, needRefineProblem, subjectSearchOnOff, mode, excuteId } = options;
    if (!lucaAskChatId || (mode !== 'subject_search' && !needRefineProblem)) return () => Promise.resolve();
    const { promise, excuteRef } = createQuestionSearchPromise(lucaAskChatId, {
        recordType: subjectSearchOnOff ? 65 : 35,
        text: { belongType: 0, content: question },
        helpYouCard: subjectSearchOnOff ? undefined : { saveFlag: false },
        subjectSearch: subjectSearchOnOff ? { saveFlag: false } : undefined,
    });
    mode === 'subject_search' && excuteRef.resolve!(true);
    needRefineProblem &&
        getGlobalStore()!.publish('NavgationBar', {
            type: 'refineProblem',
            content: question,
            isCreateQa,
            lucaAskChatId,
            excuteRef,
        });
    const newPromise = promise
        .then(async (records: any) => {
            const saveRequest = records.length
                ? async () => {
                      records[0]?.sensorsData && createSubjectSearchCardSensors(records[0].sensorsData);
                      const dataSource = (records[0] as any).dataSource;
                      createQuestionSearchPromise(
                          lucaAskChatId,
                          {
                              recordType: subjectSearchOnOff ? 65 : 35,
                              askRecordId: records[0].recordId,
                              text: { belongType: 0, content: question },
                              helpYouCard: subjectSearchOnOff ? undefined : { saveFlag: true, similarProblems: dataSource },
                              subjectSearch: subjectSearchOnOff ? { saveFlag: true, subjectSearchCard: dataSource } : undefined,
                          },
                          true
                      );
                      getQaStore()!.pushQaRecords(records);
                      await delay(800);
                  }
                : null;
            if (['analysising', 'searching'].includes(options!.getCurrentStatus()) && saveRequest) {
                options!.hide();
                await saveRequest();
                getQaStore()!.isContinueExcute(excuteId) &&
                    options!.getCurrentStatus() === undefined &&
                    options!.showLoading('lucaThinking');
                return null;
            }
            return saveRequest;
        })
        .catch(error => {
            console.log('excuteQuestionSearchProcess', error);
            options!.hide();
            return null;
        });
    return async (saveQuestionSearch = true) => {
        if (saveQuestionSearch) {
            options!.showLoading('cardInserting');
            const saveRequest = await newPromise;
            options!.hide();
            await saveRequest?.();
        }
    };
};

export const createGuessYouAskPromise = (lucaAskChatId: string | undefined, params: Record<string, any>) => {
    const promise = new Promise<IQaRecordItem[]>(async (resolve, reject) => {
        const source = getQaStore()!.addRequestController<'axios'>('axios');
        const { records, error } = await getResponseOfCreateLucaRecord(lucaAskChatId, params, { cancelToken: source.token });
        error === 'send_cancel' ? reject(error) : resolve(records || []);
    });
    return { promise };
};

export const excuteGuessYouAskProcess = (params: Record<string, any>, options: Record<string, any>) => {
    const { lucaAskChatId, guessYouAskParams } = params;
    const { needCreate = true } = options;
    if (!needCreate) return () => Promise.resolve();
    const { promise } = createGuessYouAskPromise(
        lucaAskChatId,
        guessYouAskParams || {
            recordType: 50,
            guessYouQuestion: { scene: 2, saveFlag: false },
        }
    );
    return async (isSave = true) => {
        if (!isSave) return;
        try {
            options!.showLoading('cardInserting');
            const records = await promise;
            options!.hide();
            if (records.length) {
                createGuessYouAskPromise(lucaAskChatId, {
                    recordType: 50,
                    guessYouQuestion: { saveFlag: true, relateId: (records[0] as any).relateId },
                });
                getQaStore()!.pushQaRecords(records);
                await delay(500);
            }
        } catch (error) {
            console.log('excuteGuessYouAskProcess', error);
            options!.hide();
        }
    };
};
